import Chat20 from '@carbon/icons-react/es/chat/20';
import Checkmark20 from '@carbon/icons-react/es/checkmark/20';
import Close20 from '@carbon/icons-react/es/close/20';
import Compare20 from '@carbon/icons-react/es/compare/20';
import Link20 from '@carbon/icons-react/es/link/20';
import OverflowMenuVertical20 from '@carbon/icons-react/es/overflow-menu--vertical/20';
import Time20 from '@carbon/icons-react/es/time/20';
import { Dropdown, Tooltip } from 'antd';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { FileAttachment } from 'api/attachments';
import { AttachmentType } from 'api/attachments/types';
import { Button } from 'components/atoms/button';
import { CopyToClipboard } from 'components/atoms/copy-to-clipboard';
import { Menu } from 'components/atoms/dropdown';
import { Loading } from 'components/atoms/loading';
import { RoutePaths } from 'constant';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { unreachable } from 'utils/unreachable';
import { TaskName } from '../task-name';
import {
  ActionButtonActiveWrapper,
  ActionsButtonWrapper,
  ActionsWrapper,
  AttachmentName,
  HeaderButtonWrapper,
  MenuItem,
  NameWrapper,
  TaskNameWrapper,
  Wrapper,
} from './preview-header-style-components';

interface Props {
  fileName: string;
  fileList?: FileAttachment[];
  isLoadingForAllAttachment: boolean;
  onClose: () => void;
  onOpenParentTask?: () => void;
  onDownload: () => void;
  src: string;
  isChatSectionVisible: boolean;
  onChatSectionToggle: () => void;
  isVersionSectionVisible: boolean;
  onVersionSectionToggle: () => void;
  selectedVersion: FileAttachment;
  leftAttachment?: FileAttachment;
  setLeftAttachment: (attach: FileAttachment) => void;
  rightAttachment?: FileAttachment;
  setRightAttachment: (attach: FileAttachment) => void;
}

export const PreviewHeader: React.FC<Props> = ({
  fileName,
  fileList,
  isLoadingForAllAttachment,
  onClose,
  onOpenParentTask,
  onDownload,
  isChatSectionVisible,
  onChatSectionToggle,
  isVersionSectionVisible,
  onVersionSectionToggle,
  src,
  selectedVersion,
  leftAttachment,
  setLeftAttachment,
  rightAttachment,
  setRightAttachment,
}) => {
  const history = useHistory();
  const downloadText = useTranslation('download_image');
  const openParentTaskText = useTranslation('image_preview.open_parent_task');
  const translate = useTranslate();
  const handleMenuClick = (e) => {
    switch (e.key) {
      case 'download':
        onDownload();

        return;
      case 'open-parent-task':
        onOpenParentTask?.();

        return;

      default:
        unreachable(e.key as never);
    }
  };

  const OverFlowMenu = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="download"> {downloadText} </Menu.Item>
      {selectedVersion.sourceType === AttachmentType.Task && (
        <Menu.Item key="open-parent-task"> {openParentTaskText} </Menu.Item>
      )}
    </Menu>
  );

  const onCompareVersion = useCallback(
    (version) => {
      setLeftAttachment(selectedVersion);
      setRightAttachment(version);
      if (leftAttachment && !rightAttachment) {
        setRightAttachment(version);
      }
      if (!leftAttachment && rightAttachment) {
        setLeftAttachment(version);
      }

      history.replace(
        RoutePaths.attachmentWorkspaceSideBySide
          .replace(':attachmentId', selectedVersion.attachmentId.toString())
          .replace(':compareAttachmentId', version.attachmentId.toString()),
      );
    },
    [
      history,
      leftAttachment,
      rightAttachment,
      selectedVersion,
      setLeftAttachment,
      setRightAttachment,
    ],
  );

  const MoreActionsMenu = () => {
    return (
      <Menu>
        <Menu.Header>
          <FormattedMessage id="image_preview.compare.with.version" />
        </Menu.Header>
        {/* eslint-disable-next-line react/destructuring-assignment */}
        {fileList &&
          fileList.map((version, index) => (
            <>
              {version.isDeleted === 'false' && (
                <MenuItem
                  key={version.attachmentId}
                  title={
                    <FormattedMessage
                      id="image_preview.version.no"
                      values={{ number: fileList.length - index }}
                    />
                  }
                  onClick={() => onCompareVersion(version)}
                >
                  <div>
                    <FormattedMessage
                      id="image_preview.version.no"
                      values={{ number: fileList.length - index }}
                    />
                    {version.latestVersion ? (
                      <span>
                        &nbsp;(
                        <FormattedMessage id="image_preview.current" />)
                      </span>
                    ) : null}
                  </div>
                  {selectedVersion.attachmentId === version.attachmentId && (
                    <Checkmark20 />
                  )}
                </MenuItem>
              )}
            </>
          ))}
      </Menu>
    );
  };

  return (
    <Wrapper>
      <NameWrapper>
        <AttachmentName>{fileName}</AttachmentName>

        {selectedVersion?.projectId &&
          selectedVersion?.sourceId &&
          selectedVersion.sourceType === AttachmentType.Task && (
            <TaskNameWrapper title="Task Name" onClick={onOpenParentTask}>
              <TaskName
                projectId={selectedVersion.projectId}
                taskId={selectedVersion.sourceId}
              />
            </TaskNameWrapper>
          )}
      </NameWrapper>
      <ActionsWrapper>
        <Tooltip
          title={<FormattedMessage id="image_preview.compare.version" />}
        >
          <ActionButtonActiveWrapper
            active={isVersionSectionVisible}
            onClick={onVersionSectionToggle}
          >
            <Time20 />
          </ActionButtonActiveWrapper>
        </Tooltip>

        <Tooltip
          title={<FormattedMessage id="image_preview.show_hide_comments" />}
        >
          <ActionButtonActiveWrapper
            active={isChatSectionVisible}
            onClick={onChatSectionToggle}
          >
            <Chat20 />
          </ActionButtonActiveWrapper>
        </Tooltip>

        <>
          {!isLoadingForAllAttachment && fileList ? (
            <Dropdown
              overlay={MoreActionsMenu}
              trigger={['click']}
              placement="bottomRight"
            >
              <HeaderButtonWrapper>
                <Tooltip
                  title={
                    <FormattedMessage id="image_preview.compare.with.version" />
                  }
                >
                  <Button type="text" icon={<Compare20 />} size="small" />
                </Tooltip>
              </HeaderButtonWrapper>
            </Dropdown>
          ) : (
            <Loading size="small" />
          )}
          <HeaderButtonWrapper>
            <CopyToClipboard
              icon={<Link20 />}
              textToCopy={src}
              popoverText={translate('image_preview.copy_to_clipboard')}
            />
          </HeaderButtonWrapper>
          <Dropdown overlay={OverFlowMenu}>
            <ActionsButtonWrapper>
              <OverflowMenuVertical20 />
            </ActionsButtonWrapper>
          </Dropdown>
        </>

        <Tooltip title="Close">
          <ActionsButtonWrapper onClick={onClose}>
            <Close20 />
          </ActionsButtonWrapper>
        </Tooltip>
      </ActionsWrapper>
    </Wrapper>
  );
};
